import { FC } from 'react'
import { Autocomplete, Box, Button, TextField, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Check } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import { useCountriesList } from 'services/metadata/metadata.hooks'
import theme from 'styles/theme'

import { cancelButtonSx, profileFieldSx, saveButtonSx } from './userForm.styles'

interface AddressValues {
  street: string
  city: string
  zipcode: string
  countryId: number
}

interface UserDetailsAddressFormProps {
  onBack: () => void
  title: string
  subtitle: string
  address: AddressValues
  onSave: (values: AddressValues) => Promise<unknown>
}

const UserDetailsAddressForm: FC<UserDetailsAddressFormProps> = ({
  onBack,
  title,
  subtitle,
  address,
  onSave
}) => {
  const { formatMessage } = useIntl()
  const muiTheme = useTheme()
  const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'))
  const { countries, getCountryById } = useCountriesList()
  const labelBg = isDesktop ? theme.r4r.greyBg : '#fff'

  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      street: address.street ?? '',
      city: address.city ?? '',
      zipcode: address.zipcode ?? '',
      country: (() => {
        const country = getCountryById(address.countryId)
        return country.id === -1 ? null : country
      })()
    },
    onSubmit: async ({ country, ...values }) => {
      await onSave({
        ...values,
        countryId: country?.id ?? address.countryId
      })
    }
  })

  const content = (
    <Box
      component='form'
      onSubmit={form.handleSubmit}
      sx={{ width: '100%', maxWidth: '560px' }}>
      {isDesktop && (
        <>
          <Typography sx={{ fontSize: '18px', fontWeight: 800, color: theme.r4r.navy }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mt: '8px', mb: '28px' }}>
            {subtitle}
          </Typography>
        </>
      )}
      {!isDesktop && (
        <Typography sx={{ fontSize: '13px', color: theme.r4r.grey, mb: '20px' }}>{subtitle}</Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <TextField
          fullWidth
          name='street'
          label={formatMessage({
            id: 'userDetailsAddressDuringRental.form.street',
            defaultMessage: 'Ulica'
          })}
          value={form.values.street}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={profileFieldSx(false, labelBg)}
        />
        <TextField
          fullWidth
          name='zipcode'
          label={formatMessage({
            id: 'userDetailsAddressDuringRental.form.zipcode',
            defaultMessage: 'Kod pocztowy'
          })}
          value={form.values.zipcode}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={profileFieldSx(false, labelBg)}
        />
        <TextField
          fullWidth
          name='city'
          label={formatMessage({
            id: 'userDetailsAddressDuringRental.form.city',
            defaultMessage: 'Miasto'
          })}
          value={form.values.city}
          onChange={form.handleChange}
          InputLabelProps={{ shrink: true }}
          sx={profileFieldSx(false, labelBg)}
        />
        <Autocomplete
          fullWidth
          disablePortal
          options={countries}
          value={form.values.country}
          getOptionLabel={(option) => option?.name || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(_, value) => form.setFieldValue('country', value)}
          renderInput={(params) => (
            <TextField
              {...params}
              name='country'
              label={formatMessage({
                id: 'userDetailsAddressDuringRental.form.country',
                defaultMessage: 'Kraj'
              })}
              InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
              sx={profileFieldSx(false, labelBg)}
            />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: '12px', mt: '20px', flexWrap: 'wrap' }}>
        <LoadingButton
          type='submit'
          loading={form.isSubmitting}
          disabled={!form.dirty || form.isSubmitting}
          startIcon={<Check sx={{ fontSize: '16px' }} />}
          sx={saveButtonSx}>
          {formatMessage({ id: 'userPersonalDetails.save', defaultMessage: 'Zapisz zmiany' })}
        </LoadingButton>
        <Button type='button' disabled={form.isSubmitting} onClick={() => form.resetForm()} sx={cancelButtonSx}>
          {formatMessage({ id: 'userPersonalDetails.cancel', defaultMessage: 'Anuluj' })}
        </Button>
      </Box>
    </Box>
  )

  if (isDesktop) {
    return content
  }

  return (
    <>
      <BackNavigationHeader label={title} onBack={onBack} />
      <BackNavigationContent>{content}</BackNavigationContent>
    </>
  )
}

export default UserDetailsAddressForm
